// §4.3 Forecasts (`/planning/forecasts`): the facility's forecast versions and
// New forecast. The editor is `forecast-editor.tsx`.

import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { Select } from "../../../../components/select";
import { EmptyState, ErrorState } from "../../../../components/states";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import { Banner } from "../../../../components/toast";
import { formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { usePlanningPermissions } from "../hooks/permissions";
import {
  createForecast,
  problemCode,
  useForecasts,
  usePlanningRefresh,
  type Forecast,
} from "../hooks/planning-queries";
import { parseWhole, useOffline } from "../lib/planning-lib";
import { PlanningHeader } from "../components/planning-header";
import { usePlanningFacility } from "../planning-layout";

export const FORECAST_STATUS_VARIANT: Readonly<Record<Forecast["status"], ChipVariant>> = {
  draft: "neutral",
  active: "ok",
  archived: "warn",
};

export function PlanningForecastsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const offline = useOffline();
  const { facility } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const canEdit = perms.has("planning.demand.manage");
  const [status, setStatus] = useState<Forecast["status"] | undefined>(undefined);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const page = cursors.length - 1;
  const list = useForecasts(facility.id, status, cursors[page]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    setCursors([undefined]);
  }, [facility.id, status]);

  useEffect(() => {
    if (list.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [list.error, navigate]);

  const open = (row: Forecast) => {
    void navigate({ to: "/planning/forecasts/$forecastId", params: { forecastId: row.id } });
  };

  const columns: DataTableColumn<Forecast>[] = [
    {
      key: "name",
      header: t("planning.forecasts.columns.name"),
      id: true,
      render: (row) => row.name,
    },
    {
      key: "status",
      header: t("planning.forecasts.columns.status"),
      render: (row) => (
        <StatusChip variant={FORECAST_STATUS_VARIANT[row.status]} data-status={row.status}>
          {t(`planning.forecastStatus.${row.status}`)}
        </StatusChip>
      ),
    },
    {
      key: "mode",
      header: t("planning.forecasts.columns.mode"),
      render: (row) => t(`planning.forecasts.modes.${row.consumption_mode}`),
    },
    {
      key: "window",
      header: t("planning.forecasts.columns.window"),
      numeric: true,
      render: (row) => t("planning.forecasts.windowDays", { count: row.consumption_window_days }),
    },
    {
      key: "lines",
      header: t("planning.forecasts.columns.lines"),
      numeric: true,
      render: (row) => row.line_count,
    },
    {
      key: "updated",
      header: t("planning.forecasts.columns.updated"),
      age: true,
      render: (row) => formatTimestamp(row.updated_at, i18n.language),
    },
  ];

  const empty =
    status !== undefined ? (
      <EmptyState
        title={t("planning.forecasts.filteredEmptyTitle")}
        body={t("planning.forecasts.filteredEmptyBody", {
          status: t(`planning.forecastStatus.${status}`),
        })}
        ctaLabel={t("planning.common.clearFilters")}
        onCta={() => {
          setStatus(undefined);
        }}
      />
    ) : (
      <EmptyState
        title={t("planning.forecasts.emptyTitle")}
        body={t("planning.forecasts.emptyBody")}
        ctaLabel={canEdit && !offline ? t("planning.forecasts.new") : undefined}
        onCta={
          canEdit && !offline
            ? () => {
                setCreating(true);
              }
            : undefined
        }
      />
    );

  return (
    <section className="planning-screen" data-testid="planning-forecasts">
      <PlanningHeader
        title={t("planning.forecasts.title")}
        actions={
          canEdit ? (
            <Button
              variant="primary"
              disabled={offline}
              data-testid="planning-forecast-new"
              onClick={() => {
                setCreating(true);
              }}
            >
              {t("planning.forecasts.new")}
            </Button>
          ) : undefined
        }
      />
      {canEdit && offline ? (
        <p className="planning-note">{t("planning.common.offlineWrites")}</p>
      ) : null}
      <div className="filter-bar">
        <Field label={t("planning.forecasts.filterStatus")}>
          <Select
            value={status ?? ""}
            data-testid="planning-forecasts-status"
            onChange={(e) => {
              const v = e.target.value;
              setStatus(v === "" ? undefined : (v as Forecast["status"]));
            }}
          >
            <option value="">{t("planning.common.all")}</option>
            <option value="draft">{t("planning.forecastStatus.draft")}</option>
            <option value="active">{t("planning.forecastStatus.active")}</option>
            <option value="archived">{t("planning.forecastStatus.archived")}</option>
          </Select>
        </Field>
      </div>
      {list.error !== null && !(list.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("planning.forecasts.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void list.refetch()}
          requestId={requestIdOf(list.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={list.data?.items ?? []}
          rowKey={(r) => r.id}
          rowTestId="planning-forecast-row"
          loading={list.isPending}
          loadingLabel={t("planning.forecasts.loading")}
          empty={empty}
          caption={t("planning.forecasts.title")}
          onRowClick={open}
          pager={{
            range: t("planning.common.page", { page: page + 1 }),
            page: page + 1,
            onPage: (n) => {
              if (n > page + 1 && list.data?.next_cursor)
                setCursors([...cursors, list.data.next_cursor]);
              else if (n < page + 1) setCursors(cursors.slice(0, -1));
            },
            prevLabel: t("planning.common.previous"),
            nextLabel: t("planning.common.next"),
            hasPrev: page > 0,
            hasNext: (list.data?.next_cursor ?? null) !== null,
          }}
        />
      )}
      {creating ? (
        <NewForecastDialog
          facilityId={facility.id}
          onClose={() => {
            setCreating(false);
          }}
          onCreated={(row) => {
            setCreating(false);
            open(row);
          }}
        />
      ) : null}
    </section>
  );
}

function NewForecastDialog(props: {
  facilityId: string;
  onClose: () => void;
  onCreated: (row: Forecast) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const refresh = usePlanningRefresh();
  const [name, setName] = useState("");
  const [mode, setMode] = useState<Forecast["consumption_mode"]>("consume");
  const [windowDays, setWindowDays] = useState("14");
  const [problem, setProblem] = useState<
    { field: "name" | "window" | "form"; key: string } | undefined
  >(undefined);
  const [pending, setPending] = useState(false);

  const submit = async () => {
    const trimmed = name.trim();
    if (trimmed.length === 0 || trimmed.length > 120) {
      setProblem({ field: "name", key: "planning.forecasts.errors.name" });
      return;
    }
    const days = parseWhole(windowDays, 0, 91);
    if (days === undefined) {
      setProblem({ field: "window", key: "planning.forecasts.errors.window" });
      return;
    }
    setProblem(undefined);
    setPending(true);
    try {
      const row = await createForecast({
        facility_id: props.facilityId,
        name: trimmed,
        consumption_mode: mode,
        consumption_window_days: days,
      });
      void refresh.forecasts(props.facilityId);
      props.onCreated(row);
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      if (problemCode(error) === "FORECAST_NAME_TAKEN") {
        setProblem({ field: "name", key: "planning.forecasts.errors.nameTaken" });
      } else setProblem({ field: "form", key: "planning.common.saveFailedShort" });
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal
      label={t("planning.forecasts.new")}
      heading={t("planning.forecasts.new")}
      closeLabel={t("planning.common.close")}
      onClose={props.onClose}
      pending={pending}
      className="planning-dialog"
      testId="planning-forecast-new-dialog"
    >
      {problem?.field === "form" ? <Banner kind="bad">{t(problem.key)}</Banner> : null}
      <div className="planning-form">
        <div className="planning-field">
          <Field label={t("planning.forecasts.fields.name")} wide>
            <input
              type="text"
              value={name}
              maxLength={120}
              aria-invalid={problem?.field === "name" || undefined}
              data-testid="planning-forecast-name"
              onChange={(e) => {
                setName(e.target.value);
                setProblem(undefined);
              }}
            />
          </Field>
          {problem?.field === "name" ? (
            <span className="field-error" role="alert" data-testid="planning-forecast-name-error">
              {t(problem.key)}
            </span>
          ) : null}
        </div>
        <div className="planning-field">
          <Field label={t("planning.forecasts.fields.mode")}>
            <Select
              value={mode}
              data-testid="planning-forecast-mode"
              onChange={(e) => {
                setMode(e.target.value as Forecast["consumption_mode"]);
              }}
            >
              <option value="consume">{t("planning.forecasts.modes.consume")}</option>
              <option value="additive">{t("planning.forecasts.modes.additive")}</option>
            </Select>
          </Field>
          <span className="planning-hint">{t(`planning.forecasts.modeHints.${mode}`)}</span>
        </div>
        <div className="planning-field">
          <Field label={t("planning.forecasts.fields.window")}>
            <input
              type="text"
              inputMode="numeric"
              value={windowDays}
              aria-invalid={problem?.field === "window" || undefined}
              data-testid="planning-forecast-window"
              onChange={(e) => {
                setWindowDays(e.target.value);
                setProblem(undefined);
              }}
            />
          </Field>
          <span className="planning-hint">{t("planning.forecasts.windowHint")}</span>
          {problem?.field === "window" ? (
            <span className="field-error" role="alert">
              {t(problem.key)}
            </span>
          ) : null}
        </div>
      </div>
      <div className="form-actions">
        <Button
          variant="primary"
          busy={pending}
          busyLabel={t("planning.common.saving")}
          data-testid="planning-forecast-create"
          onClick={() => void submit()}
        >
          {t("planning.forecasts.create")}
        </Button>
        <Button onClick={props.onClose}>{t("planning.common.cancel")}</Button>
      </div>
    </Modal>
  );
}
